import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';
import { fetchExercisesCategoriesMin } from '../optionsSlice';

// Async thunk to fetch exercise categories
export const fetchExerciseCategories = createAsyncThunk(
  'exerciseCategories/fetchExerciseCategories',
  async ({page = 1, searchTerm=''}, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/exercise-categories-list?page=${page}`, {searchTerm});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch exercise categories');
    }
  }
);

// Async thunk to delete an exercise category
export const deleteExerciseCategory = createAsyncThunk(
  'exerciseCategories/deleteExerciseCategory',
  async (categoryId, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`${process.env.REACT_APP_API_BASE_URL}/admin/exercise-categories/`+categoryId);
        var p = {"page":1, "searchTerm":""};
        dispatch(fetchExerciseCategories(p));
        dispatch(fetchExercisesCategoriesMin());
      return categoryId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete exercise category');
    }
  }
);

// Async thunk to create a new exercise category
export const createExerciseCategory = createAsyncThunk(
  'exerciseCategories/createExerciseCategory',
  async (categoryData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/exercise-categories`, categoryData);
      if (response.data.success) {
        var p = {"page":1, "searchTerm":""};
        dispatch(fetchExerciseCategories(p));
        dispatch(fetchExercisesCategoriesMin());
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create exercise category');
    }
  }
);

// Async thunk to update an existing exercise category
export const updateExerciseCategory = createAsyncThunk(
  'exerciseCategories/updateExerciseCategory',
  async (categoryData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/update-exercise-categories`, categoryData);
      if (response.data.success) {
        var p = {"page":1, "searchTerm":""};
        dispatch(fetchExerciseCategories(p));
        dispatch(fetchExercisesCategoriesMin());
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update exercise category');
    }
  }
);

const exerciseCategorySlice = createSlice({
  name: 'exerciseCategories',
  initialState: {
    categories: [],
    currentCategoryPage: 1,
    totalCategoryPages: 1,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExerciseCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExerciseCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data.data || []; // Ensure categories are set even if data is undefined
        state.currentCategoryPage = action.payload.data.current_page || 1; // Default to 1 if current_page is undefined
        state.totalCategoryPages = action.payload.data.last_page || 0; // Default to 0 if last_page is undefined
      })
      .addCase(fetchExerciseCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteExerciseCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(category => category.id !== action.payload);
      })
      .addCase(deleteExerciseCategory.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createExerciseCategory.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = 'Exercise category created successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(createExerciseCategory.rejected, (state, action) => {
        state.error = action.payload; // Handle error on rejection
      })
      .addCase(updateExerciseCategory.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.categories.findIndex(category => category.id === action.payload.data.id);
          if (index !== -1) {
            state.categories[index] = action.payload.data; // Update with new data
          }
          state.successMessage = 'Exercise category updated successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(updateExerciseCategory.rejected, (state, action) => {
        state.error = action.payload; // Handle error on rejection
      });
  },
});

// Export actions and reducer
export const { clearMessages } = exerciseCategorySlice.actions;

export default exerciseCategorySlice.reducer;